/**
 * Given a list of parameter values (GQL or REST), return the value that matches a given config (by ID).
 *
 * If not found, return null. This case is expected since parameter values can be sparse.
 *
 * Additionally, get RESTConfig associated to the RESTValue.
 */
import {assert} from 'Asserts';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {RESTAnalyticsParameterValue} from 'insights/parameters/RESTParameterTypes';
import {JSONValue} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

function baseGetValueJsonForConfig<T extends {valueJson: JSONValue}>(
  configIdGetter: (parameterValue: T) => string,
  parameterValues: Array<T>,
  configId: string
): JSONValue {
  const match = parameterValues.find((parameterValue) => configIdGetter(parameterValue) === configId);
  return match ? match.valueJson : null;
}

export function gqlGetValueJsonForConfig(
  parameterValues: Array<AnalyticsParameterValue>,
  configId: string
): JSONValue {
  return baseGetValueJsonForConfig((parameterValue) => parameterValue.config.id, parameterValues, configId);
}

export function getConfigForRESTValue(
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
  parameterValue: RESTAnalyticsParameterValue
): AnalyticsParameterConfigGraphQL {
  const parameterConfig = parameterConfigs.find((config) => config.id === parameterValue.configId);
  assert(parameterConfig !== undefined, 'Could not find config associated to ParameterValue');
  return parameterConfig;
}

export function restGetValueJsonForConfig(
  parameterValues: Array<RESTAnalyticsParameterValue>,
  configId: string
): JSONValue {
  return baseGetValueJsonForConfig((parameterValue) => parameterValue.configId, parameterValues, configId);
}
